// Do primeiro link do texto ao cartão na tela (exibição: bolha do chat, atendimento, post e comentário do feed).
// A tela entrega o `PrimeiroLink` (`lib/links`) e a origem da sessão; aqui se decide se há cartão:
//   - sem link, ou com usuário e senha: nada (o link segue clicável no texto);
//   - o X que o autor deu naquele link (`dispensado`): nada e nenhum pedido;
//   - o interruptor desligado (ou ainda não sabido): nada e nenhum pedido; ligado, o da casa mostra o nome da tela
//     sem rede e o de fora vem do servidor (lote + cache em `usePreviaDeLink`);
//   - o link é do SE_WEB mas não é tela do app (o arquivo assinado `/api/...`, `/health`, um estático): nada e nenhum
//     pedido. É arquivo, não página: nem o cartão da casa, nem o servidor baixando o nosso próprio arquivo;
//   - o servidor não achou título: nada.
// Só o PRIMEIRO link tem cartão: se ele falha, o segundo não é consultado (a tela só entrega o primeiro).
//
// 🔴 O componente de fora NÃO chama hook de query: as suítes antigas da bolha, do atendimento e do comentário
// renderizam sem `QueryClientProvider`, e uma mensagem sem link tem de continuar assim. Só com link se monta o
// interno, que chama `useCartoesLigados` e `usePreviaDeLink` SEMPRE (sem condicional: regra dos hooks).
// 🔴 O cartão DESMONTA (devolve `null`) quando deve sumir, nunca fica escondido: o interruptor que vira `false`
// com o cartão na tela faz `usePreviaDeLink` devolver `cartao: null` e o cartão sai, sem recarregar.
import { useCartoesLigados } from "@/hooks/useCartoesLigados";
import { usePreviaDeLink } from "@/hooks/usePreviaDeLink";
import { mesmoLink, type PrimeiroLink } from "@/lib/links/links";
import {
  ehTelaDoLink,
  previaDaCasa,
  previaDoSite,
  type AparenciaDoCartao,
} from "@/lib/links/previaDoCartao";
import type { OrigemDasPrevias } from "@/services/previasDeLinkApi";

import { CartaoDeLink } from "./CartaoDeLink";

interface Props {
  /** O primeiro link do texto (nulo = a mensagem não tem link). */
  link: PrimeiroLink | null;
  /** O `link_dispensado` gravado na mensagem (o ✕ do autor): vale enquanto o primeiro link for o mesmo. */
  dispensado?: string | null;
  origem: OrigemDasPrevias;
  aparencia: AparenciaDoCartao;
  posicao: "em-cima" | "embaixo";
  /** Da mensagem, do post ou do comentário: vira o `data-testid`. */
  id: string;
  forcarCompacta?: boolean;
}

export function CartaoDoLink({ link, ...resto }: Props): JSX.Element | null {
  if (link === null) return null;
  return <CartaoDoLinkCarregado link={link} {...resto} />;
}

function CartaoDoLinkCarregado({
  link,
  dispensado,
  origem,
  aparencia,
  posicao,
  id,
  forcarCompacta,
}: Props & { link: PrimeiroLink }) {
  // `null` = ainda não se sabe: não consulta e não mostra nada (nunca "desligado para sempre").
  const ligada = useCartoesLigados(origem);
  const tiradoPeloAutor = mesmoLink(link.normalizado, dispensado);
  const { cartao } = usePreviaDeLink(link, origem, {
    habilitado: ligada === true && !tiradoPeloAutor,
  });

  if (ligada !== true || tiradoPeloAutor || link.comCredencial) return null;
  // `usePreviaDeLink` não consulta link da casa (tela ou não); aqui só a TELA da casa ganha o cartão da casa.
  const previa = link.casa
    ? ehTelaDoLink(link)
      ? previaDaCasa(link)
      : null
    : cartao && previaDoSite(link, cartao);
  if (!previa) return null;

  return (
    <CartaoDeLink
      previa={previa}
      aparencia={aparencia}
      posicao={posicao}
      id={id}
      forcarCompacta={forcarCompacta}
    />
  );
}
